<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in navigationRoutes.routes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${route.displayName}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ route.displayName }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaCollapse v-if="childRoute.children && childRoute.children.length > 0">
              <template #header="{ value: childIsCollapsed }">
                <VaSidebarItem
                  :to="undefined"
                  :active="routeHasActiveChild(childRoute)"
                  :active-color="activeColor"
                  :text-color="textColor(childRoute)"
                  :aria-label="`Open category ${childRoute.displayName}`"
                  role="button"
                  hover-opacity="0.10"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-11">
                    <VaIcon
                      v-if="childRoute.meta.icon"
                      aria-hidden="true"
                      :name="childRoute.meta.icon"
                      size="20px"
                      :color="iconColor(childRoute)"
                    />
                    <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                      {{ childRoute.displayName }}
                      <VaIcon v-if="childRoute.children" :name="arrowDirection(childIsCollapsed)" size="20px" />
                    </VaSidebarItemTitle>
                  </VaSidebarItemContent>
                </VaSidebarItem>
              </template>
              <template #body>
                <div v-for="(grandchildRoute, index3) in childRoute.children" :key="index3">
                  <VaCollapse v-if="grandchildRoute.children && grandchildRoute.children.length > 0">
                    <template #header="{ value: grandchildIsCollapsed }">
                      <VaSidebarItem
                        :to="undefined"
                        :active="routeHasActiveChild(grandchildRoute)"
                        :active-color="activeColor"
                        :text-color="textColor(grandchildRoute)"
                        :aria-label="`Open category ${grandchildRoute.displayName}`"
                        role="button"
                        hover-opacity="0.10"
                      >
                        <VaSidebarItemContent class="py-3 pr-2 pl-16">
                          <VaIcon
                            v-if="grandchildRoute.meta?.icon"
                            aria-hidden="true"
                            :name="grandchildRoute.meta.icon"
                            size="20px"
                            :color="iconColor(grandchildRoute)"
                          />
                          <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                            {{ grandchildRoute.displayName }}
                            <VaIcon
                              v-if="grandchildRoute.children"
                              :name="arrowDirection(grandchildIsCollapsed)"
                              size="20px"
                            />
                          </VaSidebarItemTitle>
                        </VaSidebarItemContent>
                      </VaSidebarItem>
                    </template>
                    <template #body>
                      <div v-for="(greatGrandchildRoute, index4) in grandchildRoute.children" :key="index4">
                        <VaSidebarItem
                          :to="{ name: greatGrandchildRoute.name }"
                          :active="isActiveChildRoute(greatGrandchildRoute)"
                          :active-color="activeColor"
                          :text-color="textColor(greatGrandchildRoute)"
                          :aria-label="`Visit ${greatGrandchildRoute.displayName}`"
                          hover-opacity="0.10"
                        >
                          <VaSidebarItemContent class="py-3 pr-2 pl-20">
                            <VaSidebarItemTitle class="leading-5 font-semibold">
                              {{ greatGrandchildRoute.displayName }}
                            </VaSidebarItemTitle>
                          </VaSidebarItemContent>
                        </VaSidebarItem>
                      </div>
                    </template>
                  </VaCollapse>
                  <VaSidebarItem
                    v-else
                    :to="{ name: grandchildRoute.name }"
                    :active="isActiveChildRoute(grandchildRoute)"
                    :active-color="activeColor"
                    :text-color="textColor(grandchildRoute)"
                    :aria-label="`Visit ${grandchildRoute.displayName}`"
                    hover-opacity="0.10"
                  >
                    <VaSidebarItemContent class="py-3 pr-2 pl-16">
                      <VaSidebarItemTitle class="leading-5 font-semibold">
                        {{ grandchildRoute.displayName }}
                      </VaSidebarItemTitle>
                    </VaSidebarItemContent>
                  </VaSidebarItem>
                </div>
              </template>
            </VaCollapse>
            <VaSidebarItem
              v-else
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${childRoute.displayName}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ childRoute.displayName }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some((child) => {
        if (child.children) {
          return child.children.some((grandchild) => route.path.endsWith(`${grandchild.name}`))
        }
        return route.path.endsWith(`${child.name}`)
      })
    }

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
